import {
  splitPartKey,
  type DraftPart,
} from "./assetContent";
import { joinPartKey } from "./namedParts";

/**
 * The two typed speech documents, read out of and written back into a draft
 * (#870).
 *
 * ## What a speech profile is on this surface
 *
 * One language's profile is the PAIR `#869` made an asset of: the synthesis
 * config the client speaks with (`conversation_agent/<lang>/tts_config.yaml`)
 * and the Hebrew pronunciation tables the engine applies before synthesis
 * (`…/tts_homographs.yaml`). Both are `structured` parts, so what is stored in
 * a `DraftPart` is JSON TEXT — this module is the pure translation between that
 * text and the named controls the editor renders, and it performs no request
 * and holds no state.
 *
 * ## THE SERVICE OWNS COMPLETENESS. THIS MODULE OWNS PRESENTATION.
 *
 * `editor_service/speech_profile.py` is the authority: it refuses an incomplete
 * or malformed pair at both author call sites with a pointer at the field that
 * failed, and the pinned engine refuses the same shapes at a candidate's
 * provider build. Nothing here re-decides that. `speechCompleteness` below
 * reports what the form can already see is unwritten so a clinician meets the
 * requirement beside the control rather than in a refusal — the same discipline
 * `RulesAssetPage` applies to a missing required language, and like that one it
 * NEVER blocks Save. A surface that refused content the server accepts would be
 * a second gate, and a surface that accepted content the server refuses is the
 * form whose refusals a clinician cannot act on.
 *
 * ## A READER THAT DROPS A KEY IS A WRITER THAT DELETES IT
 *
 * Every edit re-serialises the WHOLE document from what this module read, so a
 * reader that skipped a key it did not recognise would publish an immutable
 * version with that key missing — and the result would then pass the service's
 * own gate, because the name it refuses is exactly the one that vanished
 * (`readSubstitutionDocument`'s rule, #910 round 3). So both readers are
 * total-or-nothing: an unrecognised top-level key, an unrecognised listener
 * gender, or a value of the wrong shape makes the whole document `unreadable`,
 * the controls are withheld, and the stored bytes pass through untouched.
 *
 * ## NOTHING IS NORMALIZED, FILLED OR TRIMMED
 *
 * `speech_profile.py` states this at the export boundary — "the emitted YAML has
 * to be the document the clinician wrote" — and it has to hold here too, one
 * layer earlier. Hebrew niqqud is carried as the literal string it is, an empty
 * table is an authored statement rather than an absence, and a temperature of
 * zero survives as the integer `0` that both runtimes hash identically.
 */

/** The two roles a `speech_profile` carries, per language. The engine's names. */
export const SPEECH_CONFIG_ROLE = "tts_config";
export const SPEECH_HOMOGRAPHS_ROLE = "tts_homographs";

/**
 * What the Model control actually does, said once for the two surfaces that
 * offer it — the profile editor and the create dialog.
 *
 * THE FIELD IS REAL AND OPTIONAL. `tts_config.yaml` may carry `model:`, but
 * the engine's own `speech_config_problem` stopped requiring it at aa0ee141
 * (liv-brain#476). A written value is exported as authored; an empty one is
 * left out of the document. The control stays so a profile that carries the
 * key can still be read and edited.
 *
 * WHAT IT NO LONGER DOES is choose the vendor model a conversation is spoken
 * with. That moved to the Tenant Manifest: liv-api resolves provider, model and
 * location through its own `TtsPolicyService` and takes only voice, temperature,
 * language and instructions from the pack (liv1.0#1165), and liv-brain#316
 * stopped serving the pack's model at all. So a clinician typing here is
 * recording what the profile is written for — not selecting what the deployment
 * will use — and the description says exactly that rather than implying a
 * control the runtime ignores.
 */
export const MODEL_FIELD_DESCRIPTION =
  "Optional. Recorded in this profile's exported pack when written. " +
  "It does not choose the model a conversation is spoken with — the tenant deployment does.";

/**
 * WHAT A PROFILE ACTUALLY REPLACES, said once for the three surfaces that say it.
 *
 * The three sentences below used to speak of "the deployment's own speech
 * settings", replaced "entirely" and carrying "all of them". That was written
 * when the pack owned every one of those settings, and it is now the sentence
 * that undoes `MODEL_FIELD_DESCRIPTION`: a clinician who reads that this profile
 * replaces the deployment's speech settings entirely, and then sees a control
 * labelled Model, has been told the model is one of the settings being replaced.
 *
 * WHAT IS STILL TRUE, and must survive the rewording: a profile that carries
 * BOTH speech documents makes the pinned engine DECLINE the deployment target's
 * speech parents for that language (liv-brain#398), so nothing underneath
 * supplies a value left out here. That is a statement about the target's PACK
 * DOCUMENTS — its voice, its pronunciation tables, its speaking instructions —
 * and it is unaffected by liv-brain#316.
 *
 * WHAT IS NO LONGER TRUE is the word "entirely". Provider, model and endpoint
 * are the Tenant Manifest's (liv1.0#1165) and no pack can reach them. So the
 * copy now names the documents it replaces instead of "the settings", and the
 * boundary sentence on the General tab names the model beside the provider,
 * credentials, voice and region it already listed.
 *
 * Hoisted out of the components deliberately: `pr-gate`'s web-fast job runs
 * `src/lib` and not `src/ui`, so a constant asserted here is a constant a merge
 * actually gates. Asserting only the field description is what let this
 * contradiction survive the first correction.
 */
export const PROFILE_REPLACES_SUMMARY =
  "A speech profile replaces the voice, pronunciation and speaking instructions the " +
  "deployment target supplies for the language it is written at, so it has to carry all of " +
  "them. It does not reach the provider, the model or the Azure deployment voice the tenant " +
  "speaks through. The word tables are filled in afterwards, on the profile's own page.";

/** The same boundary, in the incomplete-profile notice's own voice. */
export const PROFILE_INCOMPLETE_SUMMARY =
  "A profile that carries both speech documents replaces the deployment target's own speech " +
  "documents for that language, so nothing underneath supplies a value left out here";

/**
 * The boundary sentence under the General tab's synthesis controls.
 *
 * IT NAMES THE FIELD ABOVE IT FIRST, and that is the whole repair. "Voice"
 * means two different things in this product and this note sat directly under
 * an EDITABLE control labelled Voice while saying the deployment's own voice is
 * not set here — so the sentence appeared to deny the field beside it. Contract
 * §7 keeps "deployment region/Azure voice" tenant-owned; that is the provider's
 * own deployment resource, not the prebuilt synthesis voice (`voice: Leda`)
 * this profile authors and exports. Both readings are now spelled out, in that
 * order, so the control and the boundary stop contradicting each other.
 */
export const TARGET_OWNED_SETTINGS_NOTE =
  "The Voice above is the one this profile speaks with. Which provider is switched on, its " +
  "credentials, the model, and the Azure deployment voice and region belong to the tenant " +
  "deployment rather than to this profile, and are not set here.";

/**
 * The genderforms the runtime resolves, in the order it names them.
 *
 * `speech_profile.LISTENER_GENDERS`, and the same set `gender_clauses` is keyed
 * by. Both tables are required and their bare-form key sets must match, because
 * `tts_text._gender_homograph_subs` looks the conversation's genderform up in
 * this map — a word vocalized for one gender and absent for the other is a word
 * the runtime speaks two different ways depending on the patient.
 */
export const LISTENER_GENDERS = ["female", "male"] as const;

export type ListenerGender = (typeof LISTENER_GENDERS)[number];

/** A bare form → its niqqud-vocalized form. Both halves are literal strings. */
export type NiqqudTable = Record<string, string>;

/**
 * The synthesis config as the form holds it.
 *
 * Every scalar is a STRING here, including the temperature, because this is the
 * buffer a clinician types into and a half-typed value must survive a re-render.
 * `writeSynthesisConfig` decides what each one becomes in the stored document,
 * and an empty buffer becomes an ABSENT key rather than `""` — "not written" and
 * "written as nothing" are different statements, and only the first is true of a
 * field nobody has reached yet.
 */
export interface SpeechConfigDraft {
  model: string;
  voice: string;
  languageCode: string;
  temperature: string;
  instructions: string;
  female: string;
  male: string;
}

/** The pronunciation document as the form holds it: four named groups. */
export interface SpeechPronunciationDraft {
  listener: Record<ListenerGender, NiqqudTable>;
  speakerAnchor: string;
  speakerAfterAnchor: NiqqudTable;
  generalNiqqud: NiqqudTable;
}

/**
 * What one stored document could be made of.
 *
 * `absent` and `unreadable` are deliberately different: the first is a language
 * that carries no such document yet and the second is bytes this editor will
 * not re-serialise. A surface that conflated them would offer an empty form
 * over content it is about to destroy.
 */
export type SpeechDocumentRead<T> =
  | { kind: "ready"; draft: T }
  | { kind: "unreadable" }
  | { kind: "absent" };

const CONFIG_KEYS = [
  "model",
  "voice",
  "temperature",
  "language_code",
  "instructions",
  "gender_clauses",
] as const;

const PRONUNCIATION_KEYS = [
  "listener_homographs",
  "speaker_anchor",
  "speaker_homographs_after_anchor",
  "general_niqqud",
] as const;

/** The part carrying one role at one language, or `null`. Located by ROLE. */
export function findSpeechPart(
  parts: readonly DraftPart[],
  language: string,
  role: string,
): DraftPart | null {
  return (
    parts.find((part) => {
      const [partLanguage, partRole] = splitPartKey(part.key);
      return partLanguage === language && partRole === role;
    }) ?? null
  );
}

/** A stored buffer as a plain JSON object, or `null` when it is not one. */
function parseObject(part: DraftPart | null): Record<string, unknown> | null {
  if (part === null) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(part.text);
  } catch {
    return null;
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return null;
  }
  return parsed as Record<string, unknown>;
}

/** A string→string map, or `null` when the value is any other shape. */
function readTable(value: unknown): NiqqudTable | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const pairs = Object.entries(value as Record<string, unknown>);
  if (pairs.some(([, replacement]) => typeof replacement !== "string")) {
    return null;
  }
  // `Object.fromEntries`, never assignment: a bare form is whatever the
  // clinician wrote, and `__proto__` as an assignment target invokes the legacy
  // prototype setter instead of creating an own property — the entry would
  // vanish from the rebuilt document and be dropped by the next Save
  // (`readSubstitutionDocument`'s rule, #910).
  return Object.fromEntries(pairs) as NiqqudTable;
}

/**
 * The temperature as the clinician should see it.
 *
 * A stored NUMBER renders through `String`, which spells `0` as `0` — the one
 * spelling both runtimes hash identically and the reason `speech_profile.py`
 * refuses an integral float. A stored STRING renders verbatim: it is a value
 * the service will refuse, and showing it is how the clinician finds the field
 * the refusal points at.
 */
function readTemperature(value: unknown): string | null {
  if (value === undefined) {
    return "";
  }
  if (typeof value === "number") {
    return String(value);
  }
  if (typeof value === "string") {
    return value;
  }
  return null;
}

/** An optional stored string, as the form's buffer, or `null` for any other shape. */
function readText(value: unknown): string | null {
  if (value === undefined) {
    return "";
  }
  return typeof value === "string" ? value : null;
}

/**
 * One `tts_config` document, opened for editing.
 *
 * TOTAL OR NOTHING, for the reason at the top of this file: an unrecognised
 * key, or a value of a shape this form cannot hold, makes the whole document
 * `unreadable` rather than a partial reading whose next Save silently drops it.
 */
export function readSynthesisConfig(
  part: DraftPart | null,
): SpeechDocumentRead<SpeechConfigDraft> {
  if (part === null) {
    return { kind: "absent" };
  }
  const document = parseObject(part);
  if (document === null) {
    return { kind: "unreadable" };
  }
  if (
    Object.keys(document).some(
      (key) => !(CONFIG_KEYS as readonly string[]).includes(key),
    )
  ) {
    return { kind: "unreadable" };
  }

  const model = readText(document["model"]);
  const voice = readText(document["voice"]);
  const languageCode = readText(document["language_code"]);
  const instructions = readText(document["instructions"]);
  const temperature = readTemperature(document["temperature"]);
  if (
    model === null ||
    voice === null ||
    languageCode === null ||
    instructions === null ||
    temperature === null
  ) {
    return { kind: "unreadable" };
  }

  const clauses = document["gender_clauses"];
  let female = "";
  let male = "";
  if (clauses !== undefined) {
    if (clauses === null || typeof clauses !== "object" || Array.isArray(clauses)) {
      return { kind: "unreadable" };
    }
    const entries = clauses as Record<string, unknown>;
    if (
      Object.keys(entries).some(
        (key) => !(LISTENER_GENDERS as readonly string[]).includes(key),
      )
    ) {
      return { kind: "unreadable" };
    }
    const readFemale = readText(entries["female"]);
    const readMale = readText(entries["male"]);
    if (readFemale === null || readMale === null) {
      return { kind: "unreadable" };
    }
    female = readFemale;
    male = readMale;
  }

  return {
    kind: "ready",
    draft: { model, voice, languageCode, temperature, instructions, female, male },
  };
}

/** One `tts_homographs` document, opened for editing. Same total-or-nothing rule. */
export function readPronunciation(
  part: DraftPart | null,
): SpeechDocumentRead<SpeechPronunciationDraft> {
  if (part === null) {
    return { kind: "absent" };
  }
  const document = parseObject(part);
  if (document === null) {
    return { kind: "unreadable" };
  }
  if (
    Object.keys(document).some(
      (key) => !(PRONUNCIATION_KEYS as readonly string[]).includes(key),
    )
  ) {
    return { kind: "unreadable" };
  }

  const listener: Record<ListenerGender, NiqqudTable> = { female: {}, male: {} };
  const stored = document["listener_homographs"];
  if (stored !== undefined) {
    if (stored === null || typeof stored !== "object" || Array.isArray(stored)) {
      return { kind: "unreadable" };
    }
    const entries = stored as Record<string, unknown>;
    // A gender outside the two the runtime resolves is a table nothing reads,
    // and the service refuses it. Withholding the form is the same answer,
    // reached without destroying the entry on the way.
    if (
      Object.keys(entries).some(
        (key) => !(LISTENER_GENDERS as readonly string[]).includes(key),
      )
    ) {
      return { kind: "unreadable" };
    }
    for (const gender of LISTENER_GENDERS) {
      const table = entries[gender];
      if (table === undefined) {
        continue;
      }
      const read = readTable(table);
      if (read === null) {
        return { kind: "unreadable" };
      }
      listener[gender] = read;
    }
  }

  const speakerAnchor = readText(document["speaker_anchor"]);
  if (speakerAnchor === null) {
    return { kind: "unreadable" };
  }

  const afterAnchor =
    document["speaker_homographs_after_anchor"] === undefined
      ? {}
      : readTable(document["speaker_homographs_after_anchor"]);
  const general =
    document["general_niqqud"] === undefined
      ? {}
      : readTable(document["general_niqqud"]);
  if (afterAnchor === null || general === null) {
    return { kind: "unreadable" };
  }

  return {
    kind: "ready",
    draft: {
      listener,
      speakerAnchor,
      speakerAfterAnchor: afterAnchor,
      generalNiqqud: general,
    },
  };
}

/**
 * The stored document for one config draft.
 *
 * AN EMPTY BUFFER BECOMES AN ABSENT KEY. `""` would be refused for its length
 * and absence for being required — both refusals, and only one of them is a
 * true statement about what the clinician wrote. The distinction also survives
 * the round trip: an absent key reads back as an empty buffer, so nothing is
 * invented on the way in or on the way out.
 *
 * THE TEMPERATURE IS A NUMBER WHERE IT IS ONE. A buffer that parses to a finite
 * number is stored as that number — `0` for the source pack's zero, which is the
 * spelling `library._assert_storable_and_hashable` requires and the one
 * `build_tts_config` reads through its own `float()`. Anything else is stored
 * VERBATIM AS THE STRING it is, so the service refuses it at
 * `…data.temperature` with its own message instead of this module guessing a
 * number the clinician did not write.
 */
export function writeSynthesisConfig(draft: SpeechConfigDraft): Record<string, unknown> {
  const document: Record<string, unknown> = {};
  if (draft.model !== "") {
    document["model"] = draft.model;
  }
  if (draft.voice !== "") {
    document["voice"] = draft.voice;
  }
  const temperature = draft.temperature.trim();
  if (temperature !== "") {
    const parsed = Number(temperature);
    document["temperature"] = Number.isFinite(parsed) ? parsed : draft.temperature;
  }
  if (draft.languageCode !== "") {
    document["language_code"] = draft.languageCode;
  }
  if (draft.instructions !== "") {
    document["instructions"] = draft.instructions;
  }
  if (draft.female !== "" || draft.male !== "") {
    const clauses: Record<string, unknown> = {};
    if (draft.female !== "") {
      clauses["female"] = draft.female;
    }
    if (draft.male !== "") {
      clauses["male"] = draft.male;
    }
    document["gender_clauses"] = clauses;
  }
  return document;
}

/**
 * The stored document for one pronunciation draft.
 *
 * THE FOUR GROUPS ARE ALWAYS DECLARED, and the two listener tables with them.
 * §7 names "female/male tables alone" as the incomplete shape, and an EMPTY
 * table is a complete authored statement — "this profile substitutes nothing
 * here" — which is the reading the pinned engine's own predicate takes. The one
 * exception is the anchor: an unwritten anchor is absent rather than `""`, on
 * the rule above.
 */
export function writePronunciation(
  draft: SpeechPronunciationDraft,
): Record<string, unknown> {
  const document: Record<string, unknown> = {
    listener_homographs: {
      female: { ...draft.listener.female },
      male: { ...draft.listener.male },
    },
  };
  if (draft.speakerAnchor !== "") {
    document["speaker_anchor"] = draft.speakerAnchor;
  }
  document["speaker_homographs_after_anchor"] = { ...draft.speakerAfterAnchor };
  document["general_niqqud"] = { ...draft.generalNiqqud };
  return document;
}

/**
 * The draft with one document replaced, creating the part where there is none.
 *
 * The key is composed from a language the asset already carries and a role the
 * service names, so nothing here is the free-text part key the 2026-08-25
 * amendment forbids — the same rule `writeSubstitutionDocument` follows.
 */
export function writeSpeechDocument(
  parts: readonly DraftPart[],
  language: string,
  role: string,
  document: Record<string, unknown>,
  nextId: () => string,
): DraftPart[] {
  const text = JSON.stringify(document, null, 2);
  const existing = findSpeechPart(parts, language, role);
  if (existing !== null) {
    return parts.map((part) =>
      part.id === existing.id ? { ...part, text } : part,
    );
  }
  return [
    ...parts,
    {
      id: nextId(),
      key: joinPartKey(language, role),
      kind: "structured" as const,
      text,
    },
  ];
}

/** The parts of one complete profile at one language, ready for a create. */
export function speechProfileParts(
  language: string,
  config: SpeechConfigDraft,
  pronunciation: SpeechPronunciationDraft,
  nextId: () => string,
): DraftPart[] {
  return [
    {
      id: nextId(),
      key: joinPartKey(language, SPEECH_CONFIG_ROLE),
      kind: "structured" as const,
      text: JSON.stringify(writeSynthesisConfig(config), null, 2),
    },
    {
      id: nextId(),
      key: joinPartKey(language, SPEECH_HOMOGRAPHS_ROLE),
      kind: "structured" as const,
      text: JSON.stringify(writePronunciation(pronunciation), null, 2),
    },
  ];
}

/** An empty pronunciation draft — four declared groups, nothing substituted. */
export function emptyPronunciation(anchor = ""): SpeechPronunciationDraft {
  return {
    listener: { female: {}, male: {} },
    speakerAnchor: anchor,
    speakerAfterAnchor: {},
    generalNiqqud: {},
  };
}

/** One language a speech profile carries, and which of its two documents it has. */
export interface SpeechLanguageView {
  language: string;
  label: string;
  config: DraftPart | null;
  pronunciation: DraftPart | null;
  /** Parts at this language that are neither of the two speech roles. */
  other: DraftPart[];
}

/**
 * The languages this profile carries, in the order the parts do.
 *
 * A LANGUAGE-NEUTRAL PART IS NOT A LANGUAGE, and it is not silently adopted into
 * one either. `speech_profile._require_language` refuses such a part outright —
 * every path this family composes to is `{lang}`-templated, so a neutral key
 * becomes an immutable version that can never place — and this list would
 * otherwise have to invent a language to file it under. It is reported by
 * `unplaceableParts` instead, which is a fact the surface states rather than a
 * shape it edits.
 */
export function speechLanguages(
  parts: readonly DraftPart[],
): SpeechLanguageView[] {
  const order: string[] = [];
  const byLanguage = new Map<string, SpeechLanguageView>();
  for (const part of parts) {
    const [language] = splitPartKey(part.key);
    if (language === null) {
      continue;
    }
    let view = byLanguage.get(language);
    if (view === undefined) {
      view = {
        language,
        label: languageLabel(language),
        config: null,
        pronunciation: null,
        other: [],
      };
      byLanguage.set(language, view);
      order.push(language);
    }
    const [, role] = splitPartKey(part.key);
    if (role === SPEECH_CONFIG_ROLE) {
      view.config = part;
    } else if (role === SPEECH_HOMOGRAPHS_ROLE) {
      view.pronunciation = part;
    } else {
      view.other.push(part);
    }
  }
  return order.map((language) => byLanguage.get(language) as SpeechLanguageView);
}

/**
 * Parts this family cannot place, which the service refuses at Save.
 *
 * Stated on the surface rather than left to the refusal, because the remedy is
 * not something the form's controls can reach: a language-neutral key has to be
 * replaced, and this editor deliberately offers no control that writes one.
 */
export function unplaceableParts(parts: readonly DraftPart[]): DraftPart[] {
  return parts.filter((part) => splitPartKey(part.key)[0] === null);
}

/**
 * The product name of a language tag.
 *
 * The two the packs author, plus the tag itself for anything else — an honest
 * label rather than an invented one, which is `namedParts.humaniseRole`'s own
 * discipline and the rule `rulesContent.languageProductName` follows.
 */
export function languageLabel(language: string): string {
  if (language === "he") {
    return "Hebrew";
  }
  if (language === "en") {
    return "English";
  }
  return language;
}

/** One unwritten field, named for the control that carries it. */
export interface SpeechGap {
  /** Which tab the control lives on. */
  group: "general" | "gender" | "pronunciation";
  label: string;
}

/**
 * What this language has not written yet, in the service's own terms.
 *
 * REPORTED, NEVER ENFORCED. `speech_profile.py` is the authority and refuses the
 * same shapes with a field pointer; this is the same requirement stated beside
 * the controls, so a clinician meets it while authoring instead of at Save.
 * `RulesAssetPage`'s missing-language notice is the precedent and states the
 * rule outright: the service decides, and a surface that blocked what the server
 * accepts would be a second gate.
 *
 * THE LISTENER TABLES ARE NOT GAPS WHEN EMPTY. An empty table is a complete
 * authored statement, and the engine reads it as one. What IS reported is the
 * asymmetry the runtime cannot resolve — a bare form vocalized for one gender
 * and absent for the other is a word spoken two ways depending on the patient,
 * and `_validate_listener_tables` refuses it.
 */
export function speechCompleteness(
  config: SpeechDocumentRead<SpeechConfigDraft>,
  pronunciation: SpeechDocumentRead<SpeechPronunciationDraft>,
): SpeechGap[] {
  const gaps: SpeechGap[] = [];
  if (config.kind === "ready") {
    const draft = config.draft;
    const scalars: [keyof SpeechConfigDraft, SpeechGap][] = [
      ["voice", { group: "general", label: "Voice" }],
      ["languageCode", { group: "general", label: "Language code" }],
      ["temperature", { group: "general", label: "Temperature" }],
      ["instructions", { group: "general", label: "Speaking instructions" }],
      ["female", { group: "gender", label: "Instruction for a female listener" }],
      ["male", { group: "gender", label: "Instruction for a male listener" }],
    ];
    for (const [field, gap] of scalars) {
      if (draft[field].trim() === "") {
        gaps.push(gap);
      }
    }
  } else if (config.kind === "absent") {
    gaps.push({ group: "general", label: "Synthesis settings" });
  }

  if (pronunciation.kind === "ready") {
    const draft = pronunciation.draft;
    if (draft.speakerAnchor.trim() === "") {
      gaps.push({ group: "pronunciation", label: "Speaker anchor" });
    }
    const female = Object.keys(draft.listener.female);
    const male = Object.keys(draft.listener.male);
    // `Object.hasOwn`, never `in` — the rule `SpeechPronunciationGroups` states
    // over its Add control, and this is the other half of the same question.
    // `in` walks the prototype chain, so a table holding `toString` was
    // reported as covered by an EMPTY opposite table and this gap disappeared,
    // while the service compares the two key sets (`speech_profile.py`,
    // `The female and male listener tables must cover the same bare forms`) and
    // refuses the Save. A completeness readback that disagrees with the
    // refusal it is predicting is worse than no readback.
    const asymmetric =
      female.some((form) => !Object.hasOwn(draft.listener.male, form)) ||
      male.some((form) => !Object.hasOwn(draft.listener.female, form));
    if (asymmetric) {
      gaps.push({
        group: "pronunciation",
        label: "Matching female and male listener entries",
      });
    }
  } else if (pronunciation.kind === "absent") {
    gaps.push({ group: "pronunciation", label: "Pronunciation tables" });
  }

  return gaps;
}
